<template>
  <div class="mensagens-programadas-lote-table">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :data="rowData"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '@/grid/DataTable.vue';
import { useMensagensProgramadasLote } from '@/composables/useMensagensProgramadasLote';
import { useFiltro } from '@/composables/useFiltro';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  value: unknown;
  data?: Record<string, unknown>;
}

interface LoteRow {
  ID: Id;
  status?: string;
  status_label?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

// Emits
const emit = defineEmits<{
  edit: [id: Id];
  'data-loaded': [data: LoteRow[]];
}>();

// Composables
const { fetchBulkOperations } = useMensagensProgramadasLote();
const { initialized } = useFiltro();

// Data
const rowData = ref<LoteRow[]>([]);
const dataTableRef = ref<DataTableInstance | null>(null);

// DataTable Configuration
const tableColumns = computed(() => [
  {
    data: 'ID',
    title: t('ID'),
    width: 80,
    resizable: true,
    sortable: true,
    renderType: 'clickable_id',
    dataType: 'number'
  },
  {
    data: 'status_label',
    title: t('Status'),
    width: 120,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      const statusSeverityMap: Record<string, string> = {
        'Processando': 'warning',
        'Concluído': 'success',
        'Erro': 'danger',
        'Cancelado': 'secondary',
        'Pendente': 'info'
      };

      const value = params.value as string;
      const severity = statusSeverityMap[value] || 'info';
      return `<span class="p-badge p-badge-${severity}">${value}</span>`;
    }
  },
  {
    data: 'periodo_inicio',
    title: t('Período Início'),
    width: 130,
    resizable: true,
    sortable: true,
    renderType: 'date',
    dataType: 'date'
  },
  {
    data: 'periodo_fim',
    title: t('Período Fim'),
    width: 130,
    resizable: true,
    sortable: true,
    renderType: 'date',
    dataType: 'date'
  },
  {
    data: 'franquias',
    title: t('Franquias'),
    width: 200,
    resizable: true,
    sortable: false,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      if (!params.value) return '';
      if (Array.isArray(params.value)) {
        return params.value.map((f: string) => `<span class="p-badge p-badge-primary" style="margin: 2px;">${f}</span>`).join(' ');
      }
      return String(params.value);
    }
  },
  {
    data: 'template_nome',
    title: t('Template'),
    width: 200,
    resizable: true,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'quantidade_clientes',
    title: t('Quantidade Clientes'),
    width: 150,
    resizable: true,
    sortable: true,
    dataType: 'number'
  },
  {
    data: 'data_criacao',
    title: t('Data Criação'),
    width: 180,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date'
  },
  {
    data: 'usuario_nome',
    title: t('Usuário'),
    width: 150,
    resizable: true,
    sortable: true,
    dataType: 'text'
  }
]);

const tableConfig = computed(() => ({
  tableId: 'tabelaMensagensProgramadasLote',
  title: t('Mensagens Programadas em Lote'),
  filename: `mensagens_programadas_lote_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  heightMode: 'fill-viewport',
  pagination: true,
  pageLength: 25,
  rowSelection: 'single',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.ID;
    return id == null ? '' : `mensagens-programadas-lote-row-${id}`;
  }
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV')
}));

// Methods
const loadData = async (): Promise<void> => {
  const stack = new Error().stack;
  log('MensagensProgramadasLoteTable', 'loadData', 'Loading bulk operations', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  try {
    const data = await fetchBulkOperations() as LoteRow[];
    log('MensagensProgramadasLoteTable', 'loadData', 'Loaded data', data);
    rowData.value = data;
    emit('data-loaded', data);
  } catch (error) {
    log('MensagensProgramadasLoteTable', 'loadData', 'Error loading data', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar operações em lote') + ': ' + message);
  }
};

const handleDataLoaded = (data: LoteRow[]): void => {
  log('MensagensProgramadasLoteTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`operações em lote`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('MensagensProgramadasLoteTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar operações em lote: ') + message);
};

const handleEditClick = (id: Id): void => {
  log('MensagensProgramadasLoteTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

// Event Listeners
onMounted(() => {
  log('MensagensProgramadasLoteTable', 'onMounted', 'Component mounted', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount if filter is not initialized yet
  // The filter component will trigger load when ready
  if (initialized.value) {
    log('MensagensProgramadasLoteTable', 'onMounted', 'Filter initialized, loading data');
    loadData();
  } else {
    log('MensagensProgramadasLoteTable', 'onMounted', 'Waiting for filter initialization');
  }

  // Listen for filter updates
  eventBus.on('filter.updated', loadData);

  // Listen for refresh events
  eventBus.on('mensagens-programadas-lote.refresh', loadData);
});

onUnmounted(() => {
  log('MensagensProgramadasLoteTable', 'onUnmounted', 'Component unmounted');
  eventBus.off('filter.updated', loadData);
  eventBus.off('mensagens-programadas-lote.refresh', loadData);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.mensagens-programadas-lote-table {
  margin-top: 20px;
}
</style>
